import useLocalStore from "../store/useLocalStore";
import {getVersion} from "../utils";
import {
    Account, Chain, createPublicClient,
    encodeFunctionData,
    EncodeFunctionDataParameters,
    Hex, http,
    SignableMessage,
    TransactionSerializable,
    WalletClientConfig
} from 'viem';
import useStore from "../store/useStore";
import {ChainType, RecoveryType} from "../types/mpc";
import {toAccount} from "viem/accounts";
import {createWalletClient} from "viem";
import {useEffect, useMemo, useState} from "react";
import {retryMessage} from "../utils/wallet";
import {Hash} from "viem/types/misc";
import {SendTransactionParameters, encodeDeployData, DeployContractParameters, WriteContractParameters} from "viem";
import type {Abi, Address} from "abitype";
import matchlog from "../utils/matchlog";
import {useModal} from "../context/ModalContext";
import {HashPanel} from "../ui";
import useTransactionStore from "../store/useTransactionStore";

type isRecoveredType = () => Promise<boolean>;
type signMessageType = (params: { message: SignableMessage, chainType?: ChainType }) => Promise<Hex>;
type signTransactionType = (params: {
    transaction: TransactionSerializable,
    chainType?: ChainType
    chain: {
        id: number,
        name: string,
        nativeCurrency: {
            name: string
            symbol: string
            decimals: number
        },
        rpcUrls: {
            [key: string]: any
        }
    }
}) => Promise<`0x02${string}` | `0x01${string}` | `0x03${string}` | `0x04${string}` | import("viem").TransactionSerializedLegacy>;
type SendTransactionReturnType = Hash
type SendTransactionParametersType = Omit<SendTransactionParameters, 'account'>
type SendTransactionType = (
    transaction: SendTransactionParametersType
    // chainType?: ChainType
    // chainId?: number
) => Promise<SendTransactionReturnType>
type DeployContractReturnType = SendTransactionReturnType
type DeployContractType = <const abi extends Abi | readonly unknown[]>(
    parameters: DeployContractParameters<abi>
) => Promise<DeployContractReturnType>;
type WriteContractParametersType = Omit<WriteContractParameters, 'account' | 'chain'> & {
    chain?: Chain
}
type WriteContractReturnType = SendTransactionReturnType
type WriteContractType = (params: WriteContractParametersType) => Promise<WriteContractReturnType>

interface UseWalletReturnType {
    walletReady: boolean
    isRecovered: isRecoveredType
    signMessage: signMessageType
    signTransaction: signTransactionType
    address: string;
    evmAccount: Account | undefined;
    createWalletClient: (parameters: Omit<WalletClientConfig, 'account'>) => Omit<
        ReturnType<typeof createWalletClient>, 'sendTransaction' | 'deployContract' | 'writeContract'> &
        {
            sendTransaction: SendTransactionType,
            deployContract: DeployContractType
            writeContract: WriteContractType
        } | undefined
    // exportWallet:()=>Promise<void>
}

const AppClientId = "react-sdk-" + getVersion()

export default function useWallet(): UseWalletReturnType {
    const {address, wallet: walletConfig} = useLocalStore();
    const {walletReady} = useStore()
    const modal = useModal()
    const {addTransaction} = useTransactionStore()

    const isRecovered: isRecoveredType = async () => {
        const res = await window.matchProvider.waitUntilWalletMessage({
            method: 'isRecovered'
        })
        return res.isRecovered
    }

    const signMessage: signMessageType = async ({
                                                    message,
                                                    chainType = ChainType.Ethereum
                                                }) => {
        try {
            const res = await window.matchProvider.waitUntilWalletMessage({
                method: 'signMessage',
                data: {
                    message,
                    chainType
                }
            })

            return res.message
        } catch (error: any) {
            console.error('sdk.wallet.message', error)
            throw error
        }

    }

    const signTransaction: signTransactionType = async ({
                                                            transaction,
                                                            chainType = ChainType.Ethereum,
                                                            chain
                                                        }) => {
        try {
            const res = await window.matchProvider.waitUntilWalletMessage({
                method: 'signTransaction',
                data: {
                    transaction,
                    chainType,
                    chain
                }
            })
            return res.message
        } catch (error: any) {
            console.error('sdk.wallet.transaction', error)
            throw error
        }
    }

    const evmAccount = useMemo(() => {
        try{
            return address ? toAccount({
                address: address as `0x${string}`,
                async signMessage({message}) {
                    return await signMessage({
                        message,
                        chainType: ChainType.Ethereum
                    })
                },
                async signTransaction(transaction, options) {
                    //@ts-ignore
                    const {account, chain, ...restTransaction} = transaction;
                    console.log('qwe-sign-transaction', restTransaction, options)
                    return await signTransaction({
                        transaction: {
                            ...restTransaction as TransactionSerializable,
                        },
                        chainType: ChainType.Ethereum,
                        chain: {
                            id: chain?.id || restTransaction.chainId,
                            name: chain?.name,
                            nativeCurrency: {
                                name: chain?.nativeCurrency?.name || 'ETH',
                                symbol: chain?.nativeCurrency?.symbol || 'ETH',
                                decimals: chain?.nativeCurrency?.decimals || 18,
                            },
                            rpcUrls: chain?.rpcUrls
                        }
                    })
                },
                async signTypedData(typedData) {
                    return '0x'
                },
            }) : undefined
        }catch(error){
            console.error(`qwe-evm-account-error ${address}`,error)
            return undefined
        }

    }, [address])


    useEffect(() => {
        matchlog.log('qwe-evmAccount', evmAccount)
    }, [evmAccount]);


    // const exportWallet = async ()=>{
    //     await window.matchProvider.waitUntilWalletMessage({
    //         method: 'exportWallet',
    //         data: {
    //             address
    //         }
    //     })
    // }

    const realCreateWalletClient = (parameters: Omit<WalletClientConfig, 'account'>) => {
        if (!evmAccount) {
            return
        }
        // if ('chain' in parameters) {
        //     retryMessage({
        //         method: 'addChain',
        //         data: {
        //             chain: parameters.chain,
        //             create: true
        //         }
        //     })
        // }

        const obj = createWalletClient({
            ...parameters,
            account: evmAccount as Account
        })
        const sendTransaction: SendTransactionType = async (transaction) => {


            const {chain, ...restTransaction} = transaction
            const chainId = chain ? chain.id : await obj.getChainId()
            const _chain = chain || obj.chain
            const transactionId = Date.now().toString() + Math.random().toString().slice(6)
            //prepare transaction
            window.matchProvider.transactionMessageIntervalMap[transactionId] = {
                transaction,
                func: async () => {
                    try {
                        const {
                            chain,
                            account,
                            ...prepareTransactionRequest
                            // @ts-ignore
                        } = await obj.prepareTransactionRequest(window.matchProvider.transactionMessageIntervalMap[transactionId].transaction)

                        window.matchProvider.sendWalletMessage({
                            method: 'prepareTransactionRequest',
                            data: {
                                prepareTransactionRequest,
                                transactionId,
                            }
                        })
                    } catch (error) {
                        console.error(error)
                    }

                },
                interval: setInterval(() => {
                    window.matchProvider.transactionMessageIntervalMap[transactionId].func()
                }, 10000)
            }

            try {

                const {
                    chain,
                    account,
                    ...prepareTransactionRequest
                    // @ts-ignore
                } = await obj.prepareTransactionRequest(transaction)
                const {serializedTransaction} = await window.matchProvider.waitUntilWalletMessage({
                    method: 'sendTransaction',
                    data: {
                        transactionId,
                        transaction: {
                            ...restTransaction
                        },
                        chainType: ChainType.Ethereum,
                        chain: {
                            id: chainId,
                            name: _chain?.name,
                            nativeCurrency: _chain?.nativeCurrency,
                            rpcUrls: _chain?.rpcUrls
                        },
                        prepareTransactionRequest
                    }
                })
                //send transaction
                try{
                    const txHash = await obj.sendRawTransaction({
                        serializedTransaction
                    })
                    addTransaction({
                        chainId:chainId,
                        hash:txHash,
                        info:{
                            from:evmAccount.address,
                            to:restTransaction.to as Address,
                            value:(restTransaction.value||0).toString(),
                            input:restTransaction.data as Hash,
                            timestamp:Math.floor(Date.now()/1000).toString(),
                            hash:txHash,
                            source:'local',
                        },
                        address:evmAccount.address

                    })

                    modal.show((props)=>{
                        return <HashPanel isOpen onClose={props.close} zIndex={props.zIndex} hash={txHash} chain={_chain}/>
                    })

                    return txHash
                }catch(error:any){
                    modal.show((props)=>{
                        return <HashPanel isOpen onClose={props.close} zIndex={props.zIndex} error={typeof error=='string'?error :(error.details||error.message)} chain={_chain}/>
                    })
                    throw error
                }


            } catch (error: any) {
                console.error('matchid-send-error', error)

                throw error
            }finally {
                clearInterval(window.matchProvider.transactionMessageIntervalMap[transactionId].interval)
                delete window.matchProvider.transactionMessageIntervalMap[transactionId]
            }

        }

        const deployContract: DeployContractType = async <const abi extends Abi | readonly unknown[]>(parameters: DeployContractParameters<abi>) => {
            const {abi, args, bytecode, ...request} =
                parameters as DeployContractParameters
            const calldata = encodeDeployData({abi, args, bytecode});
            return await sendTransaction({

                ...request,
                data: calldata,

            } as SendTransactionParametersType);

        };

        const writeContract: WriteContractType = async (parameters) => {
            const {abi, address, args, dataSuffix, functionName, ...request} =
                parameters as WriteContractParameters
            const data = encodeFunctionData({
                abi,
                args,
                functionName,
            } as EncodeFunctionDataParameters)
            return await sendTransaction({
                data: `${data}${dataSuffix ? dataSuffix.replace('0x', '') : ''}`,
                to: address,
                ...request,
            } as SendTransactionParametersType)
        }

        return {
            ...obj,
            sendTransaction,
            deployContract,
            writeContract,
        }
    }
    return {
        walletReady,
        evmAccount,
        address,
        signMessage,
        signTransaction,
        isRecovered,
        createWalletClient: realCreateWalletClient,
        // exportWallet
    };
}
